import { QueryClient, QueryCache } from '@tanstack/react-query';
import { isNotFound } from '@localagent/sdk';

/**
 * When a single-resource query 404s (resource deleted elsewhere), evict it
 * from the cache and invalidate the parent list so the UI stays consistent.
 *
 * Query key conventions:
 *   List:   ['sessions', workspaceId]         → all sessions
 *   Single: ['sessions', workspaceId, id]     → one session
 *   Single: ['workspace-projection', workspaceId, deviceId] → one canonical projection
 *   Single: ['agent-definition', name]        → one agent (list key: 'agent-definitions')
 */
const SINGLE_RESOURCE_LIST_KEY: Record<string, string> = {
  'agent-definition': 'agent-definitions',
  'agent-definition-by-id': 'agent-definitions',
  'agent-definition-config': 'agent-definitions',
  'agent-definition-config-by-id': 'agent-definitions',
};

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (_error, query) => {
      if (!isNotFound(_error)) return;

      const keyPrefix = query.queryKey[0] as string;

      // Sessions use a shared prefix: list is ['sessions', wsId], single is ['sessions', wsId, sessionId]
      // A 404 on a 3-part key means the session was deleted — invalidate the 2-part list
      if (keyPrefix === 'sessions' && query.queryKey.length >= 3) {
        const workspaceId = query.queryKey[1];
        void queryClient.invalidateQueries({ queryKey: ['sessions', workspaceId] });
        queryClient.removeQueries({ queryKey: query.queryKey });
        return;
      }

      // Other resources have distinct prefixes for single vs list
      const listKey = SINGLE_RESOURCE_LIST_KEY[keyPrefix];
      if (listKey) {
        void queryClient.invalidateQueries({ queryKey: [listKey] });
        queryClient.removeQueries({ queryKey: query.queryKey });
      }
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // Never retry 404s — the resource is gone, retrying won't help
        if (isNotFound(error)) return false;
        return failureCount < 2;
      },
    },
  },
});
